/**
 * feat-107 Slice B — Paste-from-Notion importer wizard (Paste → Review → Commit).
 *
 * Turns a pasted Notion doc (parsed by notionCurriculumParser, pure client-side
 * TS) into the curriculum hierarchy and writes it through the EXISTING per-item
 * curriculum APIs via curriculumCommitService — so the feat-018 auto-translation
 * pipeline fires on every created item (never a direct DB insert / bulk write).
 *
 * Owned by frontend-b. Mounted by Slice A's `renderImporter` hook (PasteImport-
 * Launcher passes the ImporterContext through as props). Real tailwind tokens.
 *
 * AC7 (3-step wizard) · AC9 (review: type-override/move/drop; Commit gated on
 * zero Needs-review) · AC10 (Copy template = the golden fixture, verbatim) ·
 * AC11 (per-item API writes + translation-enqueue status) · AC12 (partial-failure
 * Retry, idempotent — committed ids skipped) · AC13 (empty/parse-failed/partial).
 */

import React, { useEffect, useMemo, useState } from 'react';
import type { QuizUnit } from '../../../../services/quizContentAdminService';
import { parse } from '../../../../services/notionCurriculumParser';
import { NOTION_IMPORT_TEMPLATE } from '../../../../services/notionImportTemplate';
import {
  commitCurriculum,
  emptyCommitState,
  type CommitItemProgress,
  type CommitPlan,
  type CommitState,
  type CommitResult,
} from '../../../../services/curriculumCommitService';
import { QUIZ_TERMS, READING_BADGE, termCount } from '../../quizTerminology';
import { IconPickerPopover } from '../../IconPickerPopover';
import { glyphForKey } from '../curriculumGlyphs';
import { LESSON_ICON_CATALOG } from '../../../../services/quizContentAdminService';
import { InferredItemCard } from './InferredItemCard';
import {
  toWorking,
  computeItemFlags,
  unresolvedCount,
  tally,
  buildCommitPlan,
  normalizeRole,
  resolveRole,
  type RoleCatalog,
  type WorkingModel,
  type WorkingItem,
} from './importerModel';
import { listRolesWithActivePrompt } from '../../../../services/rehearsalAdminService';
import {
  ATTENTION_BORDER,
  ATTENTION_INK,
  ATTENTION_MARK,
  ATTENTION_SURFACE,
} from '../tracks/attentionClasses';
import {
  BULK_SELECT_LABEL,
  CHECKING_ROLES_CHIP,
  DEGRADED_ACTION,
  DEGRADED_BODY_AFTER,
  DEGRADED_BODY_BEFORE,
  DEGRADED_BODY_CODE,
  DEGRADED_LEDE,
  ROLE_SELECT_PLACEHOLDER,
  ZERO_ROLES_BODY_EMPHASIS,
  ZERO_ROLES_LEDE,
  bulkApplyLabel,
  bulkBannerBody,
  bulkBannerLede,
  zeroRolesBodyAfter,
  zeroRolesBodyBefore,
  zeroRolesSummaryHelper,
} from './roleRemapCopy';

export interface PasteImportWizardProps {
  tenantId: string;
  units: QuizUnit[];
  selectedUnitId: string | null;
  onClose: () => void;
  /** Called after at least one item committed successfully (editor refetches). */
  onImported: () => void;
}

type Step = 'paste' | 'review' | 'commit';

const STEPS: { key: Step; label: string }[] = [
  { key: 'paste', label: 'Paste' },
  { key: 'review', label: 'Review' },
  { key: 'commit', label: 'Commit' },
];

export function PasteImportWizard({
  tenantId,
  units,
  selectedUnitId,
  onClose,
  onImported,
}: PasteImportWizardProps): React.ReactElement {
  const [step, setStep] = useState<Step>('paste');

  // Step 1 state
  const [pasteText, setPasteText] = useState('');
  const [targetUnitId, setTargetUnitId] = useState(selectedUnitId ?? units[0]?.id ?? '');
  const [newLessonName, setNewLessonName] = useState('');
  const [parseError, setParseError] = useState(false);
  const [copied, setCopied] = useState(false);

  // Step 2 state
  const [model, setModel] = useState<WorkingModel | null>(null);
  // feat-248 — the tenant's prompt-backed roles. `loading` until the fetch
  // resolves; `failed` degrades OPEN (see the effect below).
  const [roleCatalog, setRoleCatalog] = useState<RoleCatalog>({ status: 'loading' });
  /** Bumped by the degraded banner's "Try again" to re-run the effect. */
  const [rolesAttempt, setRolesAttempt] = useState(0);

  /**
   * feat-248 — load the tenant's prompt-backed roles on entering Review.
   *
   * DEGRADE OPEN. A rejection sets `failed`, not an empty list: an empty list
   * is a MEASUREMENT ("this tenant has no active prompt versions") and blocks
   * commit, while a failure is IGNORANCE and must not. A network blip must not
   * make the importer unusable — it falls back to today's behaviour (free-text
   * role, commit attempts, the backend's own 400 surfaces).
   *
   * feat-251 — this used to note that the manual editor's `.catch(() => [])`
   * conflated the two states while this surface did not. That is no longer
   * true: `QuizContentManagement` now carries the same distinction via its
   * `AvailableRoles` sentinel (`undefined` = failed, `[]` = measured empty).
   * Both surfaces read the same `listRolesWithActivePrompt`, which as of
   * feat-251 points at `GET /api/tenant/{id}/rehearsal-roles` and lets errors
   * propagate rather than swallowing them.
   */
  useEffect(() => {
    if (step !== 'review') return undefined;
    let cancelled = false;
    setRoleCatalog({ status: 'loading' });
    listRolesWithActivePrompt(tenantId)
      .then((roles) => {
        if (!cancelled) setRoleCatalog({ status: 'ready', roles });
      })
      .catch(() => {
        if (!cancelled) setRoleCatalog({ status: 'failed' });
      });
    return () => {
      cancelled = true;
    };
  }, [step, tenantId, rolesAttempt]);

  // Step 3 state
  const [plan, setPlan] = useState<CommitPlan | null>(null);
  const [progress, setProgress] = useState<CommitItemProgress[]>([]);
  const [committing, setCommitting] = useState(false);
  const [committedState, setCommittedState] = useState<CommitState>(emptyCommitState());
  const [result, setResult] = useState<CommitResult | null>(null);

  const targetLessonName = newLessonName.trim()
    ? newLessonName.trim()
    : units.find((u) => u.id === targetUnitId)?.title ?? 'this Lesson';

  // ---- Step 1: parse ----
  const onParse = () => {
    const r = parse(pasteText);
    if (r.items.length === 0) {
      setParseError(true);
      setModel(null);
      return;
    }
    setParseError(false);
    setModel(toWorking(r));
    setStep('review');
  };

  const onCopyTemplate = async () => {
    try {
      await navigator.clipboard.writeText(NOTION_IMPORT_TEMPLATE);
    } catch {
      // Fallback for environments without the async clipboard API.
      const ta = document.createElement('textarea');
      ta.value = NOTION_IMPORT_TEMPLATE;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* noop */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setPasteText((prev) => (prev.trim() ? prev : NOTION_IMPORT_TEMPLATE));
    window.setTimeout(() => setCopied(false), 2000);
  };

  // ---- Step 2: edit working model ----
  const patchItem = (tempId: string, patch: Partial<WorkingItem>) => {
    setModel((m) =>
      m
        ? { ...m, items: m.items.map((i) => (i.tempId === tempId ? { ...i, ...patch } : i)) }
        : m,
    );
  };
  const dropItem = (tempId: string) => patchItem(tempId, { dropped: true });
  const patchDrillTitle = (tempId: string, title: string) => {
    setModel((m) =>
      m ? { ...m, drills: m.drills.map((d) => (d.tempId === tempId ? { ...d, title } : d)) } : m,
    );
  };
  const patchDrillIcon = (tempId: string, iconKey: string | null) => {
    setModel((m) =>
      m ? { ...m, drills: m.drills.map((d) => (d.tempId === tempId ? { ...d, iconKey } : d)) } : m,
    );
  };

  const liveItems = model?.items.filter((i) => !i.dropped) ?? [];
  const unresolved = unresolvedCount(liveItems, roleCatalog);
  const counts = model ? tally(model, roleCatalog) : null;
  const liveRehearsals = liveItems.filter((i) => i.itemType === 'rehearsal');
  // feat-248 §5.6 — LOADING IS NOT FAILURE. While the fetch is in flight the
  // role list is unknown, so no flag can fire yet and Commit must wait rather
  // than arm on incomplete information. It re-enables on success OR failure.
  //
  // The gate is scoped to pastes that actually CONTAIN a rehearsal: §5.10 rules
  // that zero rehearsals means "no banner and no gate" even for a tenant with
  // zero roles, and a paste of pure questions is untouched by the roles fetch
  // either way (AT8's second half, AT10). Waiting on a fetch whose answer
  // cannot change any flag would be a gate with nothing behind it, and the
  // "Checking roles…" chip that explains the wait would have nothing to explain.
  const rolesPending = roleCatalog.status === 'loading' && liveRehearsals.length > 0;
  const canCommit = !!model && liveItems.length > 0 && unresolved === 0 && !rolesPending;

  /**
   * feat-248 §5.4 — one banner per DISTINCT unresolved value, grouped on the
   * RAW pasted string so the banner can quote what the user actually typed.
   * Sorted by descending count, then alphabetically: the biggest single win
   * goes on top.
   *
   * Values that differ only by case or separator (`Crew Member` vs
   * `crew-member`) are DISTINCT banners even though they normalize alike —
   * quoting a string the user never typed is worse than showing two banners,
   * and each still names exactly what appears on its own cards.
   *
   * An EMPTY role gets no banner (§5.3): there is no offending string to name,
   * and the existing `rehearsal_missing_role` flag already covers it.
   */
  const unresolvedGroups = useMemo(() => {
    if (roleCatalog.status !== 'ready' || roleCatalog.roles.length === 0) return [];
    // Derived from `model` INSIDE the memo rather than from the `liveRehearsals`
    // array above: that array is rebuilt on every render, so depending on it
    // would give a useMemo that never once hits its cache — correct, but a
    // memo-shaped lie about what the code does.
    const rehearsals = (model?.items ?? []).filter((i) => !i.dropped && i.itemType === 'rehearsal');
    const byValue = new Map<string, number>();
    for (const item of rehearsals) {
      const pasted = item.role.trim();
      if (!pasted) continue;
      if (resolveRole(pasted, roleCatalog.roles) !== null) continue;
      byValue.set(pasted, (byValue.get(pasted) ?? 0) + 1);
    }
    return [...byValue.entries()]
      .map(([pasted, count]) => ({ pasted, count }))
      .sort((a, b) => b.count - a.count || a.pasted.localeCompare(b.pasted));
  }, [model, roleCatalog]);

  /**
   * feat-248 §5.7/§5.8 — the banner's select STAGES; only this writes. If
   * picking a role alone mutated the four cards, the user could not tell
   * whether the click landed. Afterwards the banner disappears and nothing
   * replaces it: that disappearance plus four cleared chips, four cleared left
   * borders, the counter dropping to 0 and Commit arming is five simultaneous
   * confirmations. No toast — the importer has no toast pattern.
   *
   * Matched on the RAW pasted value, so applying to one banner never touches
   * the cards belonging to another (§5.9).
   */
  const applyRoleToAll = (pasted: string, roleKey: string) => {
    setModel((m) =>
      m
        ? {
            ...m,
            items: m.items.map((i) =>
              !i.dropped && i.itemType === 'rehearsal' && i.role.trim() === pasted
                ? { ...i, role: roleKey }
                : i,
            ),
          }
        : m,
    );
  };

  // ---- Step 3: commit ----
  const startCommit = async (commitPlan: CommitPlan, prior?: CommitState) => {
    setCommitting(true);
    setResult(null);
    const res = await commitCurriculum(tenantId, commitPlan, setProgress, prior);
    setCommittedState(res.state);
    setResult(res);
    setCommitting(false);
    if (res.succeeded > 0) onImported();
  };

  const onCommit = () => {
    if (!model) return;
    // feat-187 (R7): `units` (already in props, no new fetch) is what lets the
    // plan place a new Lesson at max+1 and append Drills after the target
    // Lesson's existing ones instead of everything landing at sort_order 0.
    // feat-248 (AT5): the catalog is what turns a pasted `Cashier` into the
    // committed `cashier`. Without it the payload would carry the pasted text.
    const built = buildCommitPlan(model, { unitId: targetUnitId, newLessonName }, { units }, roleCatalog);
    if (built.drills.length === 0) return;
    setPlan(built);
    setStep('commit');
    void startCommit(built, emptyCommitState());
  };

  const onRetryAll = () => {
    if (plan) void startCommit(plan, committedState);
  };

  const resetForMore = () => {
    setStep('paste');
    setPasteText('');
    setNewLessonName('');
    setParseError(false);
    setModel(null);
    setPlan(null);
    setProgress([]);
    setResult(null);
    setCommittedState(emptyCommitState());
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
      <div className="bg-cream w-full max-w-5xl rounded-3xl shadow-xl my-6 flex flex-col max-h-[92vh]">
        {/* Header + step indicator */}
        <header className="bg-white border-b border-softBorder rounded-t-3xl px-6 py-3 flex items-center justify-between">
          <div>
            <h2 className="font-display text-xl font-semibold leading-none">Bulk import — paste from Notion</h2>
            <ol className="flex items-center gap-2 text-sm mt-2">
              {STEPS.map((s, i) => {
                const active = s.key === step;
                const done = STEPS.findIndex((x) => x.key === step) > i;
                return (
                  <li key={s.key} className="flex items-center gap-2">
                    <span
                      className={`w-5 h-5 rounded-full grid place-items-center text-[11px] font-display font-semibold ${
                        active || done ? 'bg-coral text-white' : 'bg-gray-200 text-inkMuted'
                      }`}
                    >
                      {i + 1}
                    </span>
                    <span className={active ? 'font-bold text-ink' : 'text-inkMuted'}>{s.label}</span>
                    {i < STEPS.length - 1 && <span className="text-inkMuted/40 ml-1">—</span>}
                  </li>
                );
              })}
            </ol>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close importer"
            className="text-inkMuted hover:text-ink text-xl leading-none px-2"
          >
            ✕
          </button>
        </header>

        <div className="overflow-y-auto p-6">
          {step === 'paste' && (
            <StepPaste
              units={units}
              targetUnitId={targetUnitId}
              setTargetUnitId={setTargetUnitId}
              newLessonName={newLessonName}
              setNewLessonName={setNewLessonName}
              pasteText={pasteText}
              setPasteText={setPasteText}
              parseError={parseError}
              onParse={onParse}
              onCopyTemplate={onCopyTemplate}
              copied={copied}
            />
          )}

          {step === 'review' && model && (
            <StepReview
              model={model}
              pasteText={pasteText}
              targetLessonName={targetLessonName}
              counts={counts!}
              unresolved={unresolved}
              canCommit={canCommit}
              onBack={() => setStep('paste')}
              onCommit={onCommit}
              patchItem={patchItem}
              dropItem={dropItem}
              patchDrillTitle={patchDrillTitle}
              patchDrillIcon={patchDrillIcon}
              roleCatalog={roleCatalog}
              rolesPending={rolesPending}
              unresolvedGroups={unresolvedGroups}
              rehearsalCount={liveRehearsals.length}
              onApplyRoleToAll={applyRoleToAll}
              onRetryRoles={() => setRolesAttempt((n) => n + 1)}
            />
          )}

          {step === 'commit' && plan && (
            <StepCommit
              plan={plan}
              progress={progress}
              committing={committing}
              result={result}
              targetLessonName={targetLessonName}
              onRetryAll={onRetryAll}
              onImportMore={resetForMore}
              onViewLesson={() => { onImported(); onClose(); }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Step 1 — Paste
// ============================================================

function StepPaste(props: {
  units: QuizUnit[];
  targetUnitId: string;
  setTargetUnitId: (v: string) => void;
  newLessonName: string;
  setNewLessonName: (v: string) => void;
  pasteText: string;
  setPasteText: (v: string) => void;
  parseError: boolean;
  onParse: () => void;
  onCopyTemplate: () => void;
  copied: boolean;
}): React.ReactElement {
  const { units, targetUnitId, setTargetUnitId, newLessonName, setNewLessonName, pasteText, setPasteText, parseError, onParse, onCopyTemplate, copied } = props;
  const canParse = pasteText.trim().length > 0;

  return (
    <div className="space-y-5">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-bold mb-1.5" htmlFor="import-target-unit">
            Import into {QUIZ_TERMS.lesson.singular}
          </label>
          <select
            id="import-target-unit"
            value={targetUnitId}
            onChange={(e) => setTargetUnitId(e.target.value)}
            disabled={!!newLessonName.trim()}
            className="w-full bg-white border border-softBorder rounded-2xl px-4 py-2.5 text-sm font-semibold disabled:opacity-50"
          >
            {units.length === 0 && <option value="">No existing {QUIZ_TERMS.lesson.plural}</option>}
            {units.map((u) => (
              <option key={u.id} value={u.id}>{u.title}</option>
            ))}
          </select>
          <p className="text-xs text-inkMuted mt-1">
            Existing <span className="font-bold">{QUIZ_TERMS.lesson.singular}</span> (lvl 1). New {QUIZ_TERMS.drill.plural} get appended.
          </p>
        </div>
        <div>
          <label className="block text-sm font-bold mb-1.5" htmlFor="import-new-lesson">
            …or create a new {QUIZ_TERMS.lesson.singular}
          </label>
          <input
            id="import-new-lesson"
            type="text"
            value={newLessonName}
            onChange={(e) => setNewLessonName(e.target.value)}
            placeholder={`New ${QUIZ_TERMS.lesson.singular} name`}
            className="w-full bg-white border border-softBorder rounded-2xl px-4 py-2.5 text-sm"
          />
          <p className="text-xs text-inkMuted mt-1">
            Leave blank to use the dropdown. <span className="italic">Mascot picked later.</span>
          </p>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-sm font-bold" htmlFor="import-paste">Paste your Notion content</label>
          <button
            type="button"
            onClick={onCopyTemplate}
            className="text-xs font-semibold text-coral hover:underline"
          >
            {copied ? '✓ Copied template' : '⧉ Copy template for Notion'}
          </button>
        </div>
        <textarea
          id="import-paste"
          rows={16}
          value={pasteText}
          onChange={(e) => setPasteText(e.target.value)}
          placeholder="Nothing pasted yet — drop your Notion text here."
          className="w-full bg-cream border border-softBorder rounded-2xl px-4 py-3 text-[12.5px] leading-relaxed focus:ring-2 focus:ring-coral/40 focus:outline-none"
          style={{ fontFamily: 'ui-monospace,SFMono-Regular,Menlo,monospace' }}
        />
        <p className="text-xs text-inkMuted mt-1">Semi-structured is fine — the parser does its best and you fix the rest in Step 2.</p>
      </div>

      {parseError && (
        <div className="bg-danger-surface border border-danger-line/40 text-danger-text rounded-2xl p-4 text-sm">
          <p className="font-bold mb-1">✗ Couldn’t find any questions or rehearsals — check the format</p>
          <p>
            We scanned the paste but didn’t see numbered questions (<code className="font-mono">1.</code>),{' '}
            <code className="font-mono">A)–D)</code> options, or a <code className="font-mono">Rehearsal:</code> block. See{' '}
            <span className="font-semibold">What I can recognize</span> below, fix the format, and parse again.
          </p>
        </div>
      )}

      <Legend />

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onParse}
          disabled={!canParse}
          className="bg-coral text-white font-display font-semibold rounded-full px-5 py-2 text-sm hover:brightness-95 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Parse →
        </button>
      </div>
    </div>
  );
}

function Legend(): React.ReactElement {
  return (
    <details className="bg-cream border border-softBorder rounded-2xl px-4 py-3" open>
      <summary className="text-sm font-bold cursor-pointer select-none flex items-center gap-2">
        <span className="text-coral">ℹ</span> What I can recognize
      </summary>
      <ul className="mt-3 space-y-1.5 text-sm text-inkMuted">
        <li><code className="font-mono bg-coral-light rounded px-1">##</code> at line start → a new <span className="font-bold text-ink">{QUIZ_TERMS.drill.singular}</span></li>
        <li><code className="font-mono bg-blue-50 rounded px-1">1.</code> / <code className="font-mono bg-blue-50 rounded px-1">2)</code> numbered line → a <span className="font-bold text-ink">{QUIZ_TERMS.question.singular}</span></li>
        <li><code className="font-mono bg-gray-100 rounded px-1">A) B) C) D)</code> → <span className="font-bold text-ink">options</span> (multiple-choice <em>and</em> fill-blank)</li>
        <li><code className="font-mono bg-gray-100 rounded px-1">Correct: B</code> → marks the right answer (the canonical answer for a fill-blank)</li>
        <li><code className="font-mono bg-gray-100 rounded px-1">Praise:</code> → right-answer praise · <code className="font-mono bg-gray-100 rounded px-1">Explain:</code> → wrong-answer explanation</li>
        <li><code className="font-mono bg-gray-100 rounded px-1">___</code> in a question → a <span className="font-bold text-ink">Fill-blank</span> (add <code className="font-mono bg-gray-100 rounded px-1">A) B) C) D)</code> + <code className="font-mono bg-gray-100 rounded px-1">Correct:</code>, and <code className="font-mono bg-gray-100 rounded px-1">Accept:</code> for extra variants)</li>
        <li><code className="font-mono bg-gold-light rounded px-1">Rehearsal:</code> → a spoken-practice <span className="font-bold text-ink">{QUIZ_TERMS.rehearsal.singular}</span></li>
        <li><code className="font-mono bg-gray-100 rounded px-1">Role:</code> / <code className="font-mono bg-gray-100 rounded px-1">Scenario:</code> → rehearsal fields</li>
        <li><code className="font-mono bg-gray-100 rounded px-1">Correct example:</code> → the rehearsal’s model answer (the trainee’s “Try saying” card) — flagged for review if you leave it out</li>
        <li>bullets under <code className="font-mono bg-gray-100 rounded px-1">Good things:</code> / <code className="font-mono bg-gray-100 rounded px-1">Bad things:</code> → rehearsal checklists (use <code className="font-mono bg-gray-100 rounded px-1">- label: description</code> per bullet)</li>
        {/* feat-343 */}
        <li>
          <code className="font-mono bg-reading-light rounded px-1">Reading:</code> → a <span className="font-bold text-ink">{QUIZ_TERMS.reading.singular}</span> (body runs until <code className="font-mono bg-gray-100 rounded px-1">---</code> or the next {QUIZ_TERMS.drill.singular} / {QUIZ_TERMS.reading.singular} / {QUIZ_TERMS.rehearsal.singular}; formatting kept)
          <span className="block text-xs mt-0.5">Nothing else is parsed inside a {QUIZ_TERMS.reading.singular} — use <code className="font-mono bg-gray-100 rounded px-1">#</code> / <code className="font-mono bg-gray-100 rounded px-1">###</code> for its headings, because <code className="font-mono bg-gray-100 rounded px-1">##</code> starts a new {QUIZ_TERMS.drill.singular}. Images aren’t imported.</span>
        </li>
      </ul>
    </details>
  );
}

// ============================================================
// Step 2 — Review
// ============================================================

const RE_DRILL_LINE = /^#{2,}\s+/;
const RE_NUM_LINE = /^\d+[.)]\s*/;
const RE_OPT_LINE = /^[A-Da-d][).]\s+/;
const RE_REH_LINE = /^rehearsal\s*:/i;
// feat-343 — mirrors the parser's Reading capture mode (notionCurriculumParser):
// inside an open Reading only `---`, `Reading:`, `Rehearsal:` and an exactly-`## `
// heading end it, so body lines must NOT be painted as Questions/options.
const RE_READING_LINE = /^reading\s*:/i;
const RE_READING_DRILL_END = /^##(?!#)\s+\S/;
const READING_LINE_CLS = 'bg-reading-light border-b-2 border-reading rounded-sm px-0.5';

/** Per-line highlight classes, stateful across lines (a Reading's body is
 *  plain text no matter what it looks like). */
function lineClasses(lines: string[]): string[] {
  let inReading = false;
  return lines.map((line) => {
    const t = line.trim();
    if (inReading) {
      if (t === '---') {
        inReading = false;
        return '';
      }
      if (!(RE_READING_LINE.test(t) || RE_REH_LINE.test(t) || RE_READING_DRILL_END.test(t))) {
        return '';
      }
      inReading = false;
    }
    if (RE_READING_LINE.test(t)) {
      inReading = true;
      return READING_LINE_CLS;
    }
    return lineClass(line);
  });
}

function lineClass(line: string): string {
  const t = line.trim();
  if (RE_DRILL_LINE.test(t)) return 'bg-coral-light border-b-2 border-coral rounded-sm px-0.5';
  if (RE_REH_LINE.test(t)) return 'bg-gold-light border-b-2 border-gold rounded-sm px-0.5';
  if (RE_NUM_LINE.test(t)) return 'bg-blue-50 border-b-2 border-blue-700 rounded-sm px-0.5';
  if (RE_OPT_LINE.test(t)) return 'bg-gray-100 rounded-sm px-0.5';
  return '';
}

function HighlightedPaste({ text }: { text: string }): React.ReactElement {
  const lines = text.split('\n');
  const classes = lineClasses(lines);
  return (
    <div
      className="bg-cream border border-softBorder rounded-2xl p-4 max-h-[58vh] overflow-auto whitespace-pre-wrap text-[12.5px] leading-relaxed"
      style={{ fontFamily: 'ui-monospace,SFMono-Regular,Menlo,monospace' }}
    >
      {lines.map((line, i) => {
        const cls = classes[i];
        return (
          <div key={i}>
            {cls ? <span className={cls}>{line || ' '}</span> : (line || ' ')}
          </div>
        );
      })}
    </div>
  );
}

/** feat-248 §4 — the BLOCKING banner shell (bulk remap + zero-roles). Amber,
 *  `⚠`, `attention-*` throughout. `break-words` on the lede is required: a
 *  pasted role can be arbitrarily long, and the lede is the ONE place the value
 *  appears in full (everywhere else it is truncated — §5.5).
 *
 *  NO `role="alert"` (§10). This renders on ARRIVAL at the review step, not in
 *  response to a user action, so an alert would interrupt the screen reader's
 *  read of the step. The manual editor's `role="alert"` sits on a VALIDATION
 *  box that appears in response to typing — a different case. */
function BlockingBanner({
  lede,
  body,
  children,
}: {
  lede: React.ReactNode;
  body: React.ReactNode;
  children?: React.ReactNode;
}): React.ReactElement {
  return (
    <div
      className={`${ATTENTION_SURFACE} border ${ATTENTION_BORDER} ${ATTENTION_INK} rounded-2xl px-4 py-3 flex items-start gap-3 flex-wrap`}
    >
      <span aria-hidden="true">⚠</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold break-words">{lede}</p>
        <p className="text-sm mt-0.5">{body}</p>
      </div>
      {children && <div className="shrink-0 flex items-end gap-2">{children}</div>}
    </div>
  );
}

/**
 * feat-248 §4 — one bulk remap banner, for one distinct unresolved value.
 *
 * The select STAGES and the button WRITES (§5.7). The select is white, not
 * amber, so it reads as a control rather than as part of the message; the
 * button is the wizard's primary pill verbatim. Two coral pills never compete:
 * while any banner is up, Commit is disabled, so this is the only live primary
 * on screen.
 */
function BulkRemapBanner({
  pasted,
  count,
  roles,
  onApply,
}: {
  pasted: string;
  count: number;
  roles: string[];
  onApply: (roleKey: string) => void;
}): React.ReactElement {
  const [staged, setStaged] = useState('');
  const selectId = `bulk-remap-${normalizeRole(pasted)}`;
  return (
    <BlockingBanner lede={bulkBannerLede(pasted)} body={bulkBannerBody(count)}>
      <label className="text-xs font-semibold" htmlFor={selectId}>
        {BULK_SELECT_LABEL}
        <select
          id={selectId}
          value={staged}
          onChange={(e) => setStaged(e.target.value)}
          className="block mt-0.5 text-sm bg-white border border-softBorder rounded-lg px-2 py-1 min-w-[160px]"
        >
          {/* `disabled` so the placeholder cannot be re-selected once left. */}
          <option value="" disabled>{ROLE_SELECT_PLACEHOLDER}</option>
          {roles.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </label>
      <button
        type="button"
        onClick={() => onApply(staged)}
        disabled={!staged}
        className="bg-coral text-white font-display font-semibold rounded-full px-4 py-2 text-sm hover:brightness-95 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {bulkApplyLabel(count)}
      </button>
    </BlockingBanner>
  );
}

/**
 * feat-248 §4 — the DEGRADED banner. It must not use ANY amber.
 *
 * Per the feat-199 zero-state rule, an error asserts IGNORANCE while an empty
 * asserts a MEASUREMENT, and the two must differ on more than hue because CVD
 * erases hue. This differs from the zero-roles banner on six axes — glyph
 * (`ℹ` vs `⚠`), fill (white vs amber), ink, copy, the presence of an ACTION,
 * and whether Commit stays reachable. The last two are the ones that make them
 * diverge behaviourally, which is what actually matters: a blocking-looking
 * warning on a transient network blip is the failure mode this exists to avoid.
 */
function RolesDegradedBanner({ onRetry }: { onRetry: () => void }): React.ReactElement {
  return (
    <div className="bg-white border border-softBorder rounded-2xl px-4 py-3 flex items-start gap-3 flex-wrap">
      <span aria-hidden="true" className="text-inkMuted">ℹ</span>
      <div className="flex-1 min-w-0">
        <p className="text-ink text-sm font-bold break-words">{DEGRADED_LEDE}</p>
        <p className="text-inkMuted text-sm mt-0.5">
          {DEGRADED_BODY_BEFORE}
          <code className="font-mono bg-gray-100 rounded px-1">{DEGRADED_BODY_CODE}</code>
          {DEGRADED_BODY_AFTER}
        </p>
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="text-coral text-sm font-semibold hover:underline shrink-0 pt-0.5"
      >
        {DEGRADED_ACTION}
      </button>
    </div>
  );
}

function StepReview(props: {
  model: WorkingModel;
  pasteText: string;
  targetLessonName: string;
  counts: ReturnType<typeof tally>;
  unresolved: number;
  canCommit: boolean;
  onBack: () => void;
  onCommit: () => void;
  patchItem: (tempId: string, patch: Partial<WorkingItem>) => void;
  dropItem: (tempId: string) => void;
  patchDrillTitle: (tempId: string, title: string) => void;
  patchDrillIcon: (tempId: string, iconKey: string | null) => void;
  roleCatalog: RoleCatalog;
  rolesPending: boolean;
  unresolvedGroups: { pasted: string; count: number }[];
  rehearsalCount: number;
  onApplyRoleToAll: (pasted: string, roleKey: string) => void;
  onRetryRoles: () => void;
}): React.ReactElement {
  const { model, pasteText, targetLessonName, counts, unresolved, canCommit, onBack, onCommit, patchItem, dropItem, patchDrillTitle, patchDrillIcon, roleCatalog, rolesPending, unresolvedGroups, rehearsalCount, onApplyRoleToAll, onRetryRoles } = props;

  const drillsWithItems = model.drills.filter((d) =>
    model.items.some((i) => i.drillTempId === d.tempId && !i.dropped),
  );

  // §5.10 — zero rehearsals in the paste means no banner and no gate, even when
  // the tenant has zero roles. There is nothing for the roles to apply to.
  const zeroRoles =
    roleCatalog.status === 'ready' && roleCatalog.roles.length === 0 && rehearsalCount > 0;
  const availableRoles = roleCatalog.status === 'ready' ? roleCatalog.roles : [];

  return (
    <div className="space-y-4">
      {/* feat-248 §1 — banners are FULL-WIDTH above the two-column grid, not
          inside the right-hand column. They describe items in the inner
          `max-h-[58vh] overflow-auto` list, so they cannot live inside it and
          must be readable without scrolling it. */}
      {(zeroRoles || unresolvedGroups.length > 0 || roleCatalog.status === 'failed') && (
        <div className="space-y-3">
          {roleCatalog.status === 'failed' && <RolesDegradedBanner onRetry={onRetryRoles} />}
          {zeroRoles && (
            <BlockingBanner
              lede={ZERO_ROLES_LEDE}
              body={
                <>
                  {zeroRolesBodyBefore(rehearsalCount)}
                  {/* Bold inline, deliberately NOT a link — the importer is a
                      modal over the editor and there is no safe navigation out
                      of it mid-paste. */}
                  <span className="font-bold">{ZERO_ROLES_BODY_EMPHASIS}</span>
                  {zeroRolesBodyAfter(rehearsalCount)}
                </>
              }
            />
          )}
          {unresolvedGroups.map((g) => (
            <BulkRemapBanner
              key={g.pasted}
              pasted={g.pasted}
              count={g.count}
              roles={availableRoles}
              onApply={(roleKey) => onApplyRoleToAll(g.pasted, roleKey)}
            />
          ))}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-5">
        {/* LEFT — highlighted raw paste */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-display text-lg font-semibold">Your paste</h3>
            <div className="flex items-center gap-2 text-[11px]">
              <span className="bg-coral-light border-b-2 border-coral px-1">{QUIZ_TERMS.drill.singular}</span>
              <span className="bg-blue-50 border-b-2 border-blue-700 px-1">{QUIZ_TERMS.question.singular}</span>
              <span className="bg-gold-light border-b-2 border-gold px-1">{QUIZ_TERMS.rehearsal.singular}</span>
              <span className="bg-reading-light border-b-2 border-reading px-1">{QUIZ_TERMS.reading.singular}</span>
              <span className="bg-gray-100 px-1">option</span>
            </div>
          </div>
          <HighlightedPaste text={pasteText} />
        </div>

        {/* RIGHT — editable inferred hierarchy */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-display text-lg font-semibold">Inferred hierarchy</h3>
            <span className="text-xs text-inkMuted">click any field to edit</span>
          </div>
          <div className="flex items-center gap-1.5 text-sm mb-3">
            <span className="text-inkMuted">{QUIZ_TERMS.lesson.singular}</span>
            <span className="text-ink font-bold truncate max-w-[200px]">{targetLessonName}</span>
            <span className="text-inkMuted/50">›</span>
            <span className="text-inkMuted">{termCount(counts.drills, 'drill')}</span>
          </div>

          <div className="max-h-[58vh] overflow-auto pr-1 space-y-4">
            {drillsWithItems.map((drill) => (
              <div key={drill.tempId}>
                <div className="flex items-center gap-2 mb-2">
                  <IconPickerPopover
                    catalog={LESSON_ICON_CATALOG}
                    selection={drill.iconKey ? { kind: 'catalog', key: drill.iconKey } : null}
                    onSelect={(s) => patchDrillIcon(drill.tempId, s.kind === 'catalog' ? s.key : null)}
                    glyphForKey={glyphForKey}
                    ariaLabel={`${QUIZ_TERMS.drill.singular} icon`}
                  />
                  <input
                    aria-label={`${QUIZ_TERMS.drill.singular} title`}
                    value={drill.title}
                    onChange={(e) => patchDrillTitle(drill.tempId, e.target.value)}
                    className="font-display text-base font-semibold bg-transparent border border-transparent hover:border-softBorder focus:border-softBorder rounded-lg px-2 py-1 flex-1 focus:outline-none"
                  />
                  <span className="bg-gray-100 text-inkMuted text-xs font-bold rounded-full px-2.5 py-0.5">
                    {QUIZ_TERMS.drill.singular}
                  </span>
                </div>

                <div className="space-y-2 pl-3 border-l-2 border-softBorder ml-4">
                  {model.items
                    .filter((i) => i.drillTempId === drill.tempId && !i.dropped)
                    .map((item) => (
                      <InferredItemCard
                        key={item.tempId}
                        item={item}
                        drills={model.drills}
                        flags={computeItemFlags(item, roleCatalog)}
                        onChange={(patch) => patchItem(item.tempId, patch)}
                        onDrop={() => dropItem(item.tempId)}
                        roleCatalog={roleCatalog}
                      />
                    ))}
                </div>
              </div>
            ))}
            {drillsWithItems.length === 0 && (
              <p className="text-sm text-inkMuted italic">All items dropped — go back and paste again.</p>
            )}
          </div>
        </div>
      </div>

      {/* Summary bar */}
      <div className="border-t border-softBorder pt-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-sm flex-wrap">
          <span className="font-bold">Parsed:</span>
          <Chip>{termCount(counts.drills, 'drill')}</Chip>
          <Chip>{counts.mc + counts.fill} {(counts.mc + counts.fill) === 1 ? QUIZ_TERMS.question.singular : QUIZ_TERMS.question.plural}</Chip>
          <Chip>{termCount(counts.rehearsal, 'rehearsal')}</Chip>
          <Chip>{termCount(counts.reading, 'reading')}</Chip>
          {unresolved > 0 && (
            <span className={`${ATTENTION_MARK} text-xs font-bold rounded-full px-2.5 py-0.5`}>
              ⚠ {unresolved} need review
            </span>
          )}
          {/* feat-248 §5.6 — a neutral chip, so the disabled Commit below is
              explained rather than mysterious while the roles fetch is in
              flight. Neutral on purpose: nothing is wrong yet. */}
          {rolesPending && <Chip>{CHECKING_ROLES_CHIP}</Chip>}
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={onBack} className="bg-white border border-softBorder text-ink font-semibold rounded-full px-4 py-2 text-sm hover:bg-gray-50">
            ← Back to edit paste
          </button>
          <div className="text-right">
            <button
              type="button"
              onClick={onCommit}
              disabled={!canCommit}
              className="bg-coral text-white font-display font-semibold rounded-full px-4 py-2 text-sm hover:brightness-95 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Looks good — Commit →
            </button>
            {/* feat-248 — in the ZERO-ROLES case the generic "resolve them"
                helper is a false instruction: there is nothing the user can
                resolve on this screen. The banner carries the remedy, so this
                line only states the gate. Kept SHORT deliberately — duplicating
                the remedy here makes this group wide enough to wrap and skews
                the summary row.

                Token site 567: `text-warning-on` measured 2.066:1 on cream, a
                live AA failure. `text-attention-on` measures 5.624:1.

                NOTE for the ship-time diff: the plural bug design flagged on
                this line ("Resolve all 1 “Needs review” items") is LEFT AS-IS.
                It is out of scope, design reproduced it deliberately in the
                mockup, and fixing shipped copy inside a bugfix makes the diff
                read as a regression. Filed with design's follow-up. */}
            {zeroRoles ? (
              <p className={`text-xs ${ATTENTION_INK} mt-1`}>{zeroRolesSummaryHelper(rehearsalCount)}</p>
            ) : unresolved > 0 ? (
              <p className={`text-xs ${ATTENTION_INK} mt-1`}>Resolve all {unresolved} “Needs review” items to enable commit.</p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function Chip({ children }: { children: React.ReactNode }): React.ReactElement {
  return <span className="bg-gray-100 text-inkMuted text-xs font-bold rounded-full px-2.5 py-0.5">{children}</span>;
}

// ============================================================
// Step 3 — Commit
// ============================================================

function StepCommit(props: {
  plan: CommitPlan;
  progress: CommitItemProgress[];
  committing: boolean;
  result: CommitResult | null;
  targetLessonName: string;
  onRetryAll: () => void;
  onImportMore: () => void;
  onViewLesson: () => void;
}): React.ReactElement {
  const { plan, progress, committing, result, targetLessonName, onRetryAll, onImportMore, onViewLesson } = props;

  const total = progress.length || plan.drills.reduce((n, d) => n + 1 + d.children.length, plan.target.kind === 'new' ? 1 : 0);
  const saved = progress.filter((p) => p.status === 'saved').length;
  const failed = progress.filter((p) => p.status === 'failed').length;
  const pct = total ? Math.round((saved / total) * 100) : 0;
  const done = !committing && result !== null;
  const allOk = done && failed === 0;

  const totalItems = plan.drills.reduce((n, d) => n + d.children.length, 0);

  return (
    <div className="space-y-5">
      {/* Confirmation summary grouped by Drill */}
      <div>
        <h3 className="font-display text-lg font-semibold mb-1">
          About to write into <span className="text-coral">{targetLessonName}</span>
        </h3>
        <p className="text-sm text-inkMuted mb-4">{totalItems} items across {termCount(plan.drills.length, 'drill')}.</p>
        <div className="grid md:grid-cols-2 gap-3">
          {plan.drills.map((d) => {
            const mc = d.children.filter((c) => c.kind === 'question' && c.payload.question_type === 'multiple_choice').length;
            const fill = d.children.filter((c) => c.kind === 'question' && c.payload.question_type === 'fill_blank').length;
            const reh = d.children.filter((c) => c.kind === 'rehearsal').length;
            const rd = d.children.filter((c) => c.kind === 'reading').length;
            return (
              <div key={d.tempId} className="bg-cream border border-softBorder rounded-2xl p-4">
                <p className="font-display font-semibold">{d.label}</p>
                <ul className="mt-2 text-sm text-inkMuted space-y-0.5">
                  <li>· {mc + fill} {QUIZ_TERMS.question.plural} ({mc} MC, {fill} Fill)</li>
                  <li>· {termCount(reh, 'rehearsal')}</li>
                  <li>· {termCount(rd, 'reading')}</li>
                </ul>
              </div>
            );
          })}
        </div>
      </div>

      {/* Honesty note (info) */}
      <div className="bg-blue-50 border border-blue-200 text-blue-900 rounded-2xl p-4 text-sm">
        <p className="font-bold mb-1">How this saves — the honest version</p>
        <p>
          Each item is written through the curriculum API (<code className="font-mono">POST …/lessons</code>,{' '}
          <code className="font-mono">/questions</code>, <code className="font-mono">/rehearsals</code>) —{' '}
          <span className="font-bold">never inserted directly into the database</span> — so the feat-018 auto-translation
          pipeline runs on every item. Translations are queued as items save, which is why this is a one-by-one progress
          list, not an instant bulk insert.
        </p>
      </div>

      {/* Progress */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-display font-semibold">
            {committing ? `Saving ${saved} of ${total}…` : `Saved ${saved} of ${total}`}
          </h4>
          <span className="text-xs text-inkMuted">queuing translations as items save</span>
        </div>
        <div className="w-full h-2 rounded-full bg-gray-200 overflow-hidden mb-4">
          <div className="h-full bg-coral rounded-full transition-all" style={{ width: `${pct}%` }} />
        </div>
        <ul className="space-y-1.5 text-sm">
          {progress.map((p) => (
            <ProgressRow key={p.tempId} p={p} />
          ))}
        </ul>
      </div>

      {/* End states */}
      {allOk && (
        <div className="bg-success-light border border-success/30 rounded-2xl p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p className="font-display text-lg font-semibold flex items-center gap-2">
              <span className="text-success">✓</span> Imported {saved} items into {targetLessonName}
            </p>
            <p className="text-sm text-inkMuted mt-0.5">Translations queued — they’ll appear under Translations → as they finish.</p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <button type="button" onClick={onImportMore} className="bg-white border border-softBorder text-ink font-semibold rounded-full px-4 py-2 text-sm hover:bg-gray-50">Import more</button>
            <button type="button" onClick={onViewLesson} className="bg-coral text-white font-display font-semibold rounded-full px-4 py-2 text-sm hover:brightness-95">View {QUIZ_TERMS.lesson.singular} →</button>
          </div>
        </div>
      )}

      {done && failed > 0 && (
        <div className="space-y-3">
          {/* feat-248 §8.5 — this box STAYS in the `warning-*` family, on
              purpose. A partial commit failure is a BACKEND fault; `attention-*`
              is defined as an author-fixable problem in AUTHORED CONTENT,
              expressly against an engineering fault the author cannot fix.
              Retokenising it would make it claim the author wrote something
              wrong. They didn't — the request failed.
                fill   `bg-warning-surface` — unchanged, correct family.
                ink    `text-warning-on` (1.999:1, illegible) -> `text-ink`
                       (13.717:1), family-neutral so it claims neither meaning,
                       and matching the `text-ink/80` body on the next line so
                       the box stops being two-toned.
                border `warning-on/40` -> `warning-solid/40` — `solid` is the
                       family's designated GRAPHIC token where `-on` is
                       designated for text; the result is byte-identical to the
                       catalogued DegradedBanner recipe. */}
          <div className="bg-warning-surface border border-warning-solid/40 text-ink rounded-2xl p-4 text-sm">
            <p className="font-bold">⚠ Imported {saved} of {total} items — {failed} failed</p>
            <p className="text-ink/80">The saved items were created and their translations queued. Retry the failures below; nothing is duplicated on retry (already-saved items are skipped).</p>
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onViewLesson} className="bg-white border border-softBorder text-ink font-semibold rounded-full px-4 py-2 text-sm hover:bg-gray-50">View {QUIZ_TERMS.lesson.singular}</button>
            <button type="button" onClick={onRetryAll} className="bg-coral text-white font-display font-semibold rounded-full px-4 py-2 text-sm hover:brightness-95">Retry all failed ({failed})</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ProgressRow({ p }: { p: CommitItemProgress }): React.ReactElement {
  const badge =
    p.kind === 'rehearsal'
      ? { cls: 'bg-coral-light text-coral', label: 'Rehearsal' }
      : p.kind === 'reading'
      ? { cls: 'bg-reading-light text-reading', label: READING_BADGE }
      : p.kind === 'question'
      ? { cls: 'bg-blue-50 text-blue-700', label: 'Q' }
      : { cls: 'bg-gray-100 text-inkMuted', label: p.kind === 'unit' ? 'Lesson' : 'Drill' };

  if (p.status === 'failed') {
    return (
      <li className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-danger-surface border border-danger-line/30">
        <span className="text-danger-text">✗</span>
        <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 ${badge.cls}`}>{badge.label}</span>
        <span className="flex-1 truncate text-danger-text">{p.label}</span>
        <span className="text-danger-text text-xs">
          {p.error?.status ? `${p.error.status} — ` : ''}{p.error?.message ?? 'Save failed'}
        </span>
      </li>
    );
  }

  const icon =
    p.status === 'saved' ? <span className="text-success">✓</span>
    : p.status === 'saving' ? <span className="text-inkMuted animate-pulse">⟳</span>
    : p.status === 'skipped' ? <span className="text-inkMuted">⊘</span>
    : <span className="text-inkMuted">·</span>;

  const trailing =
    p.status === 'saved' ? <span className="text-gold text-xs font-bold">★ Translation queued</span>
    : p.status === 'saving' ? <span className="text-inkMuted text-xs">Saving…</span>
    : p.status === 'skipped' ? <span className="text-inkMuted text-xs">Skipped</span>
    : <span className="text-inkMuted text-xs">Queued</span>;

  return (
    <li className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${p.status === 'queued' || p.status === 'skipped' ? 'opacity-60' : ''}`}>
      {icon}
      <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 ${badge.cls}`}>{badge.label}</span>
      <span className="flex-1 truncate">{p.label}</span>
      {trailing}
    </li>
  );
}

export default PasteImportWizard;
